import Guard from 'arg-guard'

import Service from './service'
import ServiceOperation from './service-operation'
import Logger from '@/logger'

/**
 * Gateway for accessing Improvements data.
 * @export ImprovementService
 * @class ImprovementService
 */
export default class ImprovementService extends Service {

    /**
     * Creates an instance of ImprovementService.
     * @param {?number} schemeId
     * @memberof ImprovementService
     */
    constructor({ schemeId } = { schemeId: null }) {
        super('/improvement', 'Improvements')

        Guard.throwIf(schemeId, 'schemeId')
        this.schemeId = schemeId
    }

    /**
     * Check the status of the improvements calculation for an address ID.
     * @param {number} addressId
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof ImprovementService
     */
    async checkAnalysisCalculationAsync(addressId) {
        Guard.throwIf(addressId, 'addressId')

        const operationState = new ServiceOperation('Analysis Calculation Status Fetcher', true)
        if (addressId === 0)
            return this._failOperation(new Error('No address ID specified (0)'), operationState, 'checkAnalysisCalculationAsync')

        const url = `status/for-address/${ addressId }/in-scheme/${ this.schemeId }`
        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [GET]')

        try {
            return this._processData(await this.axios.get(url, this._buildAxiosConfig()), operationState, 'checkAnalysisCalculationAsync')
        } catch (err) {
            return this._failOperation(err, operationState, 'checkAnalysisCalculationAsync')
        }
    }

    /**
     * Trigger the calculation on the server of improvements for an address.
     * @param {number} addressId
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof ImprovementService
     */
    async ensureCalculationsAsync(addressId) {
        Guard.throwIf(addressId, 'addressId')

        const operationState = new ServiceOperation('Ensure Improvements', true)
        if (addressId === 0)
            return this._failOperation(new Error('No address ID specified (0)'), operationState, 'ensureCalculationsAsync')

        const url = `for-address/${ addressId }/in-scheme/${ this.schemeId }/ensure`
        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [POST]')

        try {
            return this._processResponse(await this.axios.post(url, {}, this._buildAxiosConfig()), operationState, 'ensureCalculationsAsync')
        } catch (err) {
            return this._failOperation(err, operationState, 'ensureCalculationsAsync')
        }
    }

    /**
     * Load improvements from an address ID.
     * @param {number} addressId
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof ImprovementService
     */
    async loadImprovementsAsync(addressId) {
        Guard.throwIf(addressId, 'addressId')

        const operationState = new ServiceOperation('Improvements Load', true)
        if (addressId === 0)
            return this._failOperation(new Error('No address ID specified (0)'), operationState, 'loadImprovementsAsync')

        const url = `for-address/${ addressId }/in-scheme/${ this.schemeId }`
        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [GET]')

        try {
            return this._processData(await this.axios.get(url, this._buildAxiosConfig()), operationState, 'loadImprovementsAsync')
        } catch (err) {
            return this._failOperation(err, operationState, 'loadImprovementsAsync')
        }
    }

    /**
     * Determines if local data is out of sync with the server so project state can be invalidated.
     * @param {number} addressId
     * @param {Date} dateStamp The date stamp from the current local version of the data.
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof ImprovementService
     */
    async localDataOutOfSyncAsync(addressId, dateStamp) {
        Guard.throwIf(addressId, 'addressId')
        Guard.throwIf(dateStamp, 'dateStamp')

        const operationState = new ServiceOperation('Check Project Data Sync State', true)
        if (addressId === 0)
            return this._failOperation(new Error('No address ID specified (0)'), operationState, 'localDataOutOfSyncAsync')

        const url = `for-address/${ addressId }/in-scheme/${ this.schemeId }/have-changed-since/${ dateStamp }`
        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [GET]')

        try {
            return this._processData(await this.axios.get(url, this._buildAxiosConfig()), operationState, 'localDataOutOfSyncAsync')
        } catch (err) {
            return this._failOperation(err, operationState, 'localDataOutOfSyncAsync')
        }
    }
}
